// store/slices/connection.ts — socket connection status.
//
// Drives the connection banner in AdminShell. Distinguishes:
//   - idle:          haven't started connecting yet
//   - connecting:    initial connection in progress
//   - connected:     handshake done, sync:initial applied; `cursor` is set
//   - reconnecting:  socket.io is trying to reconnect after a drop
//   - fatal:         we should give up; show a blocking overlay

import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

export type ConnectionState =
  | { state: "idle" }
  | { state: "connecting" }
  | { state: "connected"; cursor: string }
  | { state: "reconnecting"; lastError?: string }
  | { state: "fatal"; error: string };

const initialState: ConnectionState = { state: "idle" };

const connection = createSlice({
  name: "connection",
  initialState: initialState as ConnectionState,
  reducers: {
    connecting(): ConnectionState {
      return { state: "connecting" };
    },
    connected(_state, action: PayloadAction<string>): ConnectionState {
      return { state: "connected", cursor: action.payload };
    },
    updateCursor(state, action: PayloadAction<string>): ConnectionState {
      if (state.state === "connected") {
        return { state: "connected", cursor: action.payload };
      }
      return state;
    },
    reconnecting(
      _state,
      action: PayloadAction<string | undefined>,
    ): ConnectionState {
      return { state: "reconnecting", lastError: action.payload };
    },
    fatal(_state, action: PayloadAction<string>): ConnectionState {
      return { state: "fatal", error: action.payload };
    },
    reset(): ConnectionState {
      return { state: "idle" };
    },
  },
});

export default connection;
